// WhatsApp Web message type map: the single reference table every engine and the
// public API map FROM (no per-site ad-hoc branches).
//
// Source of truth: WhatsApp Web's own JS (WAWebVerifyProtobufMsgObjectKeys
// proto-key -> MSG_TYPE dispatch, WAWebMessagePluginParseProtobufRegistry and
// the WAWebParse*Proto parsers). Machine-extracted facts live next to this file
// in message-type-map.wa-facts.json (scripts/extract-wa-message-type-map.mjs);
// the table below is the human-reviewed, frozen form. Doc:
// docs/wa-message-type-mapping.md (build id, source modules).
//
// ALIAS rule: every row carries BOTH `waWebName` (what WA Web calls it) and
// `v1TypeString` (the string the /v1 API emits). Existing types keep the
// contract string unchanged. Interactive menus/clicks (conductor design) stay
// type chat with the text in the body and add an optional interactive block.
// EXCEED rule: where WA Web itself degrades to `unknown` the row is marked
// `baileysCanRecover` and carries `selection` / `options` projected from the
// raw proto. WA Web's UNKNOWN is the floor, not the ceiling.
//
// Dependency-free: helpers take a plain object (a decoded proto message). No
// Baileys / wa-js imports, so the extension, the backend and the Baileys pod
// can all consume this file.

import type { ButtonReply } from '../bt/user-message';

export type WaRole = 'content' | 'menu' | 'response' | 'protocol' | 'wrapper' | 'unmapped';

export const WA_INTERACTIVE_KINDS = [
    'buttons',
    'native_flow',
    'buttons_response',
    'list',
    'list_response',
    'template',
    'template_button_reply',
    'interactive',
    'interactive_response',
] as const;
export type WaInteractiveKind = (typeof WA_INTERACTIVE_KINDS)[number];

export type WaProtoScalar = string | number | boolean;

/** Gate on a value inside `content[protoKey]` (path is relative to it). */
export interface WaWhenOneOf {
    readonly path: readonly string[];
    readonly oneOf: readonly WaProtoScalar[];
}
export interface WaWhenPresent {
    readonly path: readonly string[];
    readonly present: boolean;
}
export type WaWhen = WaWhenOneOf | WaWhenPresent;

export interface WaButtonsOptionsSpec {
    readonly kind: 'buttons';
    readonly listPath: readonly string[];
    readonly idKey: string;
    readonly textPath: readonly string[];
}
export interface WaListOptionsSpec {
    readonly kind: 'list-sections';
    readonly sectionsPath: readonly string[];
    readonly rowsKey: string;
    readonly idKey: string;
    readonly titleKey: string;
    readonly descriptionKey: string;
    readonly sectionTitleKey: string;
}
export interface WaNativeFlowOptionsSpec {
    readonly kind: 'native-flow';
    readonly listPath: readonly string[];
    /** dotted key of the flow name inside each button */
    readonly nameKey: string;
    /** dotted key of the JSON string with the button params inside each button */
    readonly paramsKey: string;
}
export type WaOptionsSpec = WaButtonsOptionsSpec | WaListOptionsSpec | WaNativeFlowOptionsSpec;

export interface WaSelectionSpec {
    readonly idPath: readonly string[];
    readonly textPath: readonly string[];
    readonly descriptionPath?: readonly string[];
    readonly indexPath?: readonly string[];
    /** native-flow responses: JSON string whose `jsonIdKey` holds the selected id */
    readonly jsonPath?: readonly string[];
    readonly jsonIdKey?: string;
    readonly namePath?: readonly string[];
    readonly quotedIdPath: readonly string[];
    readonly quotedParticipantPath: readonly string[];
}

export interface WaMessageTypeRow {
    /** Stable row id: the proto key, plus `:variant` when a key has several rows. */
    readonly id: string;
    readonly protoKey: string;
    /** WAWeb module that parses this key (null: category / several parsers / none). */
    readonly parserModule: string | null;
    /** MSG_TYPE string WA Web assigns (null: WA Web assigns none, e.g. ignored keys). */
    readonly waWebName: string | null;
    /** WA Web MsgKind. */
    readonly kind: string | null;
    readonly subtype?: string;
    /** Human-readable gating copied from the parser. */
    readonly condition?: string;
    /** Machine-checkable form of the gating (first matching row for a key wins). */
    readonly when?: WaWhen;
    readonly fieldsKept: readonly string[];
    /** The string the /v1 API emits (contract). Menus/clicks are chat + an interactive block. */
    readonly v1TypeString: string;
    /** What the Baileys engine emitted before this table, when it differs. */
    readonly legacyBaileysType?: string;
    /** WA Web degrades this to unknown, but the raw proto still carries the data. */
    readonly baileysCanRecover?: boolean;
    readonly role: WaRole;
    /** Menus and clicks keep type chat (back-compat) and add an interactive block of this kind. */
    readonly interactiveKind?: WaInteractiveKind;
    /** Alternative proto paths (relative to the key) for the message text; first non-empty wins. */
    readonly textPaths?: readonly (readonly string[])[];
    readonly options?: WaOptionsSpec;
    readonly selection?: WaSelectionSpec;
}

export const WA_MESSAGE_TYPE_MAP_BUILD_ID = '1049294120';

export const WA_MESSAGE_TYPE_MAP = [
    {
        id: "messageContextInfo",
        protoKey: "messageContextInfo",
        parserModule: null,
        waWebName: null,
        kind: null,
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "statusLinkPreviewMetadata",
        protoKey: "statusLinkPreviewMetadata",
        parserModule: null,
        waWebName: null,
        kind: null,
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "contactMessage",
        protoKey: "contactMessage",
        parserModule: "WAWebParseVcardMessageProto",
        waWebName: "vcard",
        kind: "Vcard",
        fieldsKept: ["body", "vcardFormattedName"],
        v1TypeString: "vcard",
        role: "content",
    },
    {
        id: "contactsArrayMessage",
        protoKey: "contactsArrayMessage",
        parserModule: "WAWebParseMultiVcardMessageProto",
        waWebName: "multi_vcard",
        kind: "MultiVcard",
        fieldsKept: [],
        v1TypeString: "multi_vcard",
        role: "content",
    },
    {
        id: "conversation",
        protoKey: "conversation",
        parserModule: "WAWebParseChatMessageProto",
        waWebName: "chat",
        kind: "Chat",
        fieldsKept: ["body"],
        v1TypeString: "chat",
        role: "content",
    },
    {
        id: "extendedTextMessage",
        protoKey: "extendedTextMessage",
        parserModule: "WAWebParseChatMessageProto",
        waWebName: "chat",
        kind: "Chat",
        condition: "subtype url (link preview) when matchedText is present: WAWebParseExtendedTextUrlChatMessageProto",
        fieldsKept: ["body"],
        v1TypeString: "chat",
        role: "content",
    },
    {
        id: "imageMessage",
        protoKey: "imageMessage",
        parserModule: "WAWebParseImageMessageProto",
        waWebName: "image",
        kind: "Image",
        fieldsKept: ["body", "caption", "deprecatedMms3Url", "directPath", "encFilehash", "filehash", "futureproofType", "height", "interactiveAnnotations", "isViewOnce", "mediaKey", "mediaKeyTimestamp", "mimetype", "qrUrl", "scanLengths", "scansSidecar", "size", "staticUrl", "statusMentioned", "thumbnailDirectPath", "thumbnailEncSha256", "thumbnailSha256", "viewMode", "width"],
        v1TypeString: "image",
        role: "content",
    },
    {
        id: "locationMessage",
        protoKey: "locationMessage",
        parserModule: "WAWebParseLocationMessageProto",
        waWebName: "location",
        kind: "Location",
        fieldsKept: ["accuracy", "body", "clientUrl", "comment", "degrees", "isLive", "lat", "lng", "loc", "sequence", "speed"],
        v1TypeString: "location",
        role: "content",
    },
    {
        id: "liveLocationMessage",
        protoKey: "liveLocationMessage",
        parserModule: "WAWebParseLocationMessageProto",
        waWebName: "location",
        kind: "Location",
        fieldsKept: ["accuracy", "body", "clientUrl", "comment", "degrees", "isLive", "lat", "lng", "loc", "sequence", "speed"],
        v1TypeString: "live_location",
        role: "content",
    },
    {
        id: "documentMessage",
        protoKey: "documentMessage",
        parserModule: "WAWebParseDocumentMessageProto",
        waWebName: "document",
        kind: "Document",
        fieldsKept: ["body", "caption", "deprecatedMms3Url", "directPath", "encFilehash", "filehash", "filename", "isCaptionByUser", "isVcardOverMmsDocument", "mediaKey", "mediaKeyTimestamp", "mimetype", "pageCount", "size", "thumbnailDirectPath", "thumbnailEncSha256", "thumbnailHeight", "thumbnailSha256", "thumbnailWidth"],
        v1TypeString: "document",
        role: "content",
    },
    {
        id: "videoMessage",
        protoKey: "videoMessage",
        parserModule: "WAWebParseVideoMessageProto",
        waWebName: "video",
        kind: "Video",
        fieldsKept: ["accessibilityLabel", "body", "caption", "dashManifestUrl", "deprecatedMms3Url", "directPath", "duration", "encFilehash", "filehash", "futureproofType", "gifAttribution", "height", "interactiveAnnotations", "isGif", "isViewOnce", "mediaKey", "mediaKeyTimestamp", "mimetype", "size", "staticUrl", "statusMentioned", "streamingSidecar", "thumbnailDirectPath", "thumbnailEncSha256", "thumbnailSha256", "viewMode", "width"],
        v1TypeString: "video",
        role: "content",
    },
    {
        id: "highlyStructuredMessage",
        protoKey: "highlyStructuredMessage",
        parserModule: "WAWebParseHsmMessageProto",
        waWebName: "protocol",
        kind: "Hsm",
        fieldsKept: ["body"],
        v1TypeString: "notification",
        role: "protocol",
    },
    {
        id: "statusNotificationMessage",
        protoKey: "statusNotificationMessage",
        parserModule: "WAWebParseProtocolStatusNotificationMessageProto",
        waWebName: "status_notification",
        kind: null,
        fieldsKept: ["futureproofSubtype", "viewMode"],
        v1TypeString: "notification",
        role: "protocol",
    },
    {
        id: "sendPaymentMessage",
        protoKey: "sendPaymentMessage",
        parserModule: "WAWebParseSendPaymentMessageProto",
        waWebName: "payment",
        kind: "Payment",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "requestPaymentMessage",
        protoKey: "requestPaymentMessage",
        parserModule: "WAWebParseRequestPaymentMessageProto",
        waWebName: "payment",
        kind: "Payment",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "paymentInviteMessage",
        protoKey: "paymentInviteMessage",
        parserModule: "WAWebParsePaymentInviteMessageProto",
        waWebName: "payment",
        kind: "Payment",
        condition: "Message$PaymentInviteMessage$ServiceType.NOVI",
        fieldsKept: ["paymentExpiryTimestamp", "paymentInviteServiceType"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "declinePaymentRequestMessage",
        protoKey: "declinePaymentRequestMessage",
        parserModule: "WAWebParseDeclinePaymentMessageProto",
        waWebName: "unknown",
        kind: null,
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "cancelPaymentRequestMessage",
        protoKey: "cancelPaymentRequestMessage",
        parserModule: "WAWebParseCancelPaymentMessageProto",
        waWebName: "unknown",
        kind: null,
        fieldsKept: ["paymentRequestMessageKey"],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "stickerMessage",
        protoKey: "stickerMessage",
        parserModule: "WAWebStickersParseStickerMessageProto",
        waWebName: "sticker",
        kind: "Sticker",
        fieldsKept: ["deprecatedMms3Url", "directPath", "emojis", "encFilehash", "filehash", "firstFrameLength", "firstFrameSidecar", "height", "isAiSticker", "isAnimated", "isAvatar", "isLottie", "mediaKey", "mediaKeyTimestamp", "mediaPngThumbnail", "mimetype", "size", "stickerPremiumStatus", "stickerSentTs", "width"],
        v1TypeString: "sticker",
        role: "content",
    },
    {
        id: "groupInviteMessage",
        protoKey: "groupInviteMessage",
        parserModule: "WAWebParseGroupInviteMessageProto",
        waWebName: "groups_v4_invite",
        kind: "GroupsV4Invite",
        fieldsKept: ["comment", "inviteCode", "inviteCodeExp", "inviteGrp", "inviteGrpName"],
        v1TypeString: "group_invite",
        role: "content",
    },
    {
        id: "templateButtonReplyMessage",
        protoKey: "templateButtonReplyMessage",
        parserModule: "WAWebParseTemplateButtonReplyMessageProto",
        waWebName: "template_button_reply",
        kind: "TemplateButtonReply",
        fieldsKept: ["body", "selectedCarouselCardIndex", "selectedId", "selectedIndex"],
        v1TypeString: "chat",
        role: "response",
        interactiveKind: "template_button_reply",
        textPaths: [["selectedDisplayText"]],
        selection: { idPath: ["selectedId"], textPath: ["selectedDisplayText"], indexPath: ["selectedIndex"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "productMessage",
        protoKey: "productMessage",
        parserModule: "WAWebParseProductMessageProto",
        waWebName: "product",
        kind: "Product",
        fieldsKept: ["businessOwnerJid", "caption", "currencyCode", "description", "footer", "priceAmount1000", "productId", "productImageCount", "retailerId", "salePriceAmount1000", "title", "url"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "orderMessage",
        protoKey: "orderMessage",
        parserModule: "WAWebParseOrderMessageProto",
        waWebName: "order",
        kind: "Order",
        fieldsKept: ["body", "catalogType", "itemCount", "message", "orderId", "orderTitle", "sellerJid", "status", "surface", "thumbnail", "token", "totalAmount1000", "totalCurrencyCode"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "interactiveMessage",
        protoKey: "interactiveMessage",
        parserModule: "WAWebParseInteractiveMessageProto",
        waWebName: "interactive",
        kind: "Interactive",
        condition: "WA Web returns unknown/phone_only_feature when the interactive type is unrecognised/disabled or the version unsupported (AB-gated); body = interactiveMessage.body.text",
        fieldsKept: ["bizSource", "caption", "interactivePayload", "interactiveType", "pmCampaignId"],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "menu",
        interactiveKind: "interactive",
        textPaths: [["body", "text"]],
        options: { kind: "native-flow", listPath: ["nativeFlowMessage", "buttons"], nameKey: "name", paramsKey: "buttonParamsJson" },
    },
    {
        id: "interactiveResponseMessage",
        protoKey: "interactiveResponseMessage",
        parserModule: "WAWebParseInteractiveResponseMessageProto",
        waWebName: "interactive_response",
        kind: "InteractiveResponse",
        condition: "WA Web returns unknown/phone_only_feature when the response type is disabled/unsupported or the payload throws (AB-gated); the raw proto still carries body.text + nativeFlowResponseMessage",
        fieldsKept: ["body", "interactivePayload"],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "response",
        interactiveKind: "interactive_response",
        textPaths: [["body", "text"]],
        selection: { idPath: [], textPath: ["body", "text"], jsonPath: ["nativeFlowResponseMessage", "paramsJson"], jsonIdKey: "id", namePath: ["nativeFlowResponseMessage", "name"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "reactionMessage",
        protoKey: "reactionMessage",
        parserModule: "WAWebReactionsParseReactionMessageProto",
        waWebName: "reaction",
        kind: "ReactionDecrypted",
        fieldsKept: ["reactionParentKey", "reactionText", "reactionTimestamp", "read"],
        v1TypeString: "reaction",
        role: "content",
    },
    {
        id: "encReactionMessage",
        protoKey: "encReactionMessage",
        parserModule: "WAWebReactionsParseReactionEncMessageProto",
        waWebName: "reaction_enc",
        kind: "ReactionEncrypted",
        fieldsKept: ["encIv", "encPayload", "targetMessageKey"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "encCommentMessage",
        protoKey: "encCommentMessage",
        parserModule: "WAWebParseEncCommentMessageProto",
        waWebName: "comment",
        kind: "CommentEncrypted",
        fieldsKept: ["addonEncrypted", "encIv", "encPayload", "targetMessageKey"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "pinInChatMessage",
        protoKey: "pinInChatMessage",
        parserModule: "WAWebParsePinMessageProto",
        waWebName: "pin_message",
        kind: "PinInChat",
        fieldsKept: ["pinExpiryDuration", "pinMessageType", "pinParentKey", "pinSenderTimestampMs"],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "pollCreationMessage",
        protoKey: "pollCreationMessage",
        parserModule: "WAWebPollsParsePollCreationMessageProto",
        waWebName: "poll_creation",
        kind: "PollCreation",
        fieldsKept: ["correctOptionIndex", "futureproofType", "pollAllowAddOption", "pollContentType", "pollEndTime", "pollHideVoterNames", "pollName", "pollOptions", "pollSelectableOptionsCount", "pollType"],
        v1TypeString: "poll_creation",
        role: "content",
    },
    {
        id: "pollCreationMessageV2",
        protoKey: "pollCreationMessageV2",
        parserModule: "WAWebPollsParsePollCreationMessageProto",
        waWebName: "poll_creation",
        kind: "PollCreation",
        fieldsKept: ["correctOptionIndex", "futureproofType", "pollAllowAddOption", "pollContentType", "pollEndTime", "pollHideVoterNames", "pollName", "pollOptions", "pollSelectableOptionsCount", "pollType"],
        v1TypeString: "poll_creation",
        role: "content",
    },
    {
        id: "pollCreationMessageV3",
        protoKey: "pollCreationMessageV3",
        parserModule: "WAWebPollsParsePollCreationMessageProto",
        waWebName: "poll_creation",
        kind: "PollCreation",
        fieldsKept: ["correctOptionIndex", "futureproofType", "pollAllowAddOption", "pollContentType", "pollEndTime", "pollHideVoterNames", "pollName", "pollOptions", "pollSelectableOptionsCount", "pollType"],
        v1TypeString: "poll_creation",
        role: "content",
    },
    {
        id: "pollCreationMessageV5",
        protoKey: "pollCreationMessageV5",
        parserModule: "WAWebPollsParsePollCreationMessageProto",
        waWebName: "poll_creation",
        kind: "PollCreation",
        fieldsKept: ["correctOptionIndex", "futureproofType", "pollAllowAddOption", "pollContentType", "pollEndTime", "pollHideVoterNames", "pollName", "pollOptions", "pollSelectableOptionsCount", "pollType"],
        v1TypeString: "poll_creation",
        role: "content",
    },
    {
        id: "pollCreationMessageV6",
        protoKey: "pollCreationMessageV6",
        parserModule: "WAWebPollsParsePollCreationMessageProto",
        waWebName: "poll_creation",
        kind: "PollCreation",
        fieldsKept: ["correctOptionIndex", "futureproofType", "pollAllowAddOption", "pollContentType", "pollEndTime", "pollHideVoterNames", "pollName", "pollOptions", "pollSelectableOptionsCount", "pollType"],
        v1TypeString: "poll_creation",
        role: "content",
    },
    {
        id: "pollUpdateMessage",
        protoKey: "pollUpdateMessage",
        parserModule: "WAWebPollsParsePollVoteMessageProto",
        waWebName: "poll_update",
        kind: "PollVoteEncrypted",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "keepInChatMessage",
        protoKey: "keepInChatMessage",
        parserModule: "WAWebParseKeepInChatMessageProto",
        waWebName: "keep_in_chat",
        kind: "KeepInChat",
        fieldsKept: ["keepType", "keptMessageKey", "senderTimestampMs"],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "requestPhoneNumberMessage",
        protoKey: "requestPhoneNumberMessage",
        parserModule: "WAWebParseRequestPhoneNumberMessageProto",
        waWebName: "request_phone_number",
        kind: "RequestPhoneNumber",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "ptvMessage",
        protoKey: "ptvMessage",
        parserModule: "WAWebPtvParsePtvMessageProto",
        waWebName: "ptv",
        kind: "Ptv",
        fieldsKept: ["accessibilityLabel", "body", "caption", "deprecatedMms3Url", "directPath", "duration", "encFilehash", "filehash", "gifAttribution", "height", "interactiveAnnotations", "isGif", "isViewOnce", "mediaKey", "mediaKeyTimestamp", "mimetype", "size", "staticUrl", "streamingSidecar", "thumbnailDirectPath", "thumbnailEncSha256", "thumbnailSha256", "width"],
        v1TypeString: "ptv",
        role: "content",
    },
    {
        id: "callLogMesssage",
        protoKey: "callLogMesssage",
        parserModule: null,
        waWebName: "call_log",
        kind: null,
        fieldsKept: [],
        v1TypeString: "call_log",
        role: "content",
    },
    {
        id: "senderKeyDistributionMessage",
        protoKey: "senderKeyDistributionMessage",
        parserModule: null,
        waWebName: null,
        kind: null,
        condition: "WA Web dispatch category: SenderKeyDistribution",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "fastRatchetKeySenderKeyDistributionMessage",
        protoKey: "fastRatchetKeySenderKeyDistributionMessage",
        parserModule: null,
        waWebName: null,
        kind: null,
        condition: "WA Web dispatch category: SenderKeyDistribution",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "newsletterAdminInviteMessage",
        protoKey: "newsletterAdminInviteMessage",
        parserModule: "WAWebParseNewsletterAdminInviteMessageProto",
        waWebName: "newsletter_admin_invite",
        kind: "NewsletterAdminInvite",
        fieldsKept: ["futureproofType"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "newsletterFollowerInviteMessageV2",
        protoKey: "newsletterFollowerInviteMessageV2",
        parserModule: "WAWebParseNewsletterFollowerInviteMessageProto",
        waWebName: "newsletter_follower_invite",
        kind: "NewsletterFollowerInvite",
        fieldsKept: ["futureproofType"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "eventMessage",
        protoKey: "eventMessage",
        parserModule: "WAWebEventsParseEventCreationMessageProto",
        waWebName: "event_creation",
        kind: "EventCreation",
        fieldsKept: ["eventDescription", "eventEndTime", "eventExtraGuestsAllowed", "eventIsScheduledCall", "eventJoinLink", "eventLocation", "eventName", "eventStartTime", "isEventCanceled"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "eventInviteMessage",
        protoKey: "eventInviteMessage",
        parserModule: "WAWebSharableEventInviteParseEventInviteMessageProto",
        waWebName: "sharable_event_invite",
        kind: "SharableEventInvite",
        fieldsKept: ["futureproofType", "sharableEventInviteCallLink", "sharableEventInviteCaption", "sharableEventInviteEndTime", "sharableEventInviteId", "sharableEventInviteIsCanceled", "sharableEventInviteJpegThumbnail", "sharableEventInviteStartTime", "sharableEventInviteTitle"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "encEventResponseMessage",
        protoKey: "encEventResponseMessage",
        parserModule: "WAWebEventsParseEventResponseMessageProto",
        waWebName: "event_response",
        kind: "EventResponseEncrypted",
        fieldsKept: ["addonEncrypted", "encIv", "encPayload", "targetMessageKey"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "albumMessage",
        protoKey: "albumMessage",
        parserModule: "WAWebAlbumsParseAlbumMessageProto",
        waWebName: "album",
        kind: "Album",
        fieldsKept: ["expectedImageCount", "expectedVideoCount"],
        v1TypeString: "album",
        role: "content",
    },
    {
        id: "musicMessage",
        protoKey: "musicMessage",
        parserModule: "WAWebMusicParseMusicMessageProto",
        waWebName: "music",
        kind: null,
        fieldsKept: ["futureproofType"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "stickerPackMessage",
        protoKey: "stickerPackMessage",
        parserModule: "WAWebParseStickerPackMessageProto",
        waWebName: "sticker-pack",
        kind: "StickerPack",
        fieldsKept: ["caption", "deprecatedMms3Url", "description", "directPath", "encFilehash", "filehash", "filename", "isCaptionByUser", "mediaKey", "mediaKeyTimestamp", "size", "stickerPackId", "stickerPackPublisher", "stickerPackSize", "stickers", "thumbnailDirectPath", "thumbnailEncSha256", "thumbnailSha256", "trayIconFileName"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "pollResultSnapshotMessage",
        protoKey: "pollResultSnapshotMessage",
        parserModule: "WAWebParsePollResultSnapshotMessageProto",
        waWebName: "poll_result_snapshot",
        kind: "PollResultSnapshot",
        fieldsKept: ["futureproofType", "pollName", "pollType", "pollVotesSnapshot"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "pollResultSnapshotMessageV3",
        protoKey: "pollResultSnapshotMessageV3",
        parserModule: "WAWebParsePollResultSnapshotMessageProto",
        waWebName: "poll_result_snapshot",
        kind: "PollResultSnapshot",
        fieldsKept: ["futureproofType", "pollName", "pollType", "pollVotesSnapshot"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "richResponseMessage",
        protoKey: "richResponseMessage",
        parserModule: "WAWebParseRichResponseMessageProto",
        waWebName: "rich_response",
        kind: "RichResponse",
        fieldsKept: ["futureproofType", "richResponse", "unifiedResponse", "unifiedResponseMutationMediaList", "unifiedResponseRawData"],
        v1TypeString: "unknown",
        role: "content",
    },
    {
        id: "messageHistoryBundle",
        protoKey: "messageHistoryBundle",
        parserModule: "WAWebParseMessageHistoryBundleProto",
        waWebName: "group-history",
        kind: null,
        fieldsKept: ["futureproofSubtype", "futureproofType", "viewMode"],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "messageHistoryNotice",
        protoKey: "messageHistoryNotice",
        parserModule: "WAWebParseMessageHistoryNoticeProto",
        waWebName: "message_history_notice",
        kind: "MessageHistoryNotice",
        fieldsKept: ["groupHistoryBundleMetadata", "viewMode"],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "acp2SettingMessage",
        protoKey: "acp2SettingMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "associatedChildMessage",
        protoKey: "associatedChildMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "audioStickerMessage",
        protoKey: "audioStickerMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "bcallMessage",
        protoKey: "bcallMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "botForwardedMessage",
        protoKey: "botForwardedMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "botGroupParticipantMessage",
        protoKey: "botGroupParticipantMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "botInvokeMessage",
        protoKey: "botInvokeMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "botPlatformRegistrationSuccessMessage",
        protoKey: "botPlatformRegistrationSuccessMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "botTaskMessage",
        protoKey: "botTaskMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "call",
        protoKey: "call",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "chat",
        protoKey: "chat",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "commentMessage",
        protoKey: "commentMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "conditionalRevealMessage",
        protoKey: "conditionalRevealMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "deviceSentMessage",
        protoKey: "deviceSentMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "documentWithCaptionMessage",
        protoKey: "documentWithCaptionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "editedMessage",
        protoKey: "editedMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "ephemeralMessage",
        protoKey: "ephemeralMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "eventCoverImage",
        protoKey: "eventCoverImage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "groupMentionedMessage",
        protoKey: "groupMentionedMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "groupStatusMentionMessage",
        protoKey: "groupStatusMentionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "groupStatusMessage",
        protoKey: "groupStatusMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "groupStatusMessageV2",
        protoKey: "groupStatusMessageV2",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "invoiceMessage",
        protoKey: "invoiceMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "lottieStickerMessage",
        protoKey: "lottieStickerMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "newsletterAdminProfileMessage",
        protoKey: "newsletterAdminProfileMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "newsletterAdminProfileStatusMessage",
        protoKey: "newsletterAdminProfileStatusMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "newsletterScheduledMessage",
        protoKey: "newsletterScheduledMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "placeholderMessage",
        protoKey: "placeholderMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "pollCreationMessageV4",
        protoKey: "pollCreationMessageV4",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "pollCreationOptionImageMessage",
        protoKey: "pollCreationOptionImageMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "pollAddOptionMessage",
        protoKey: "pollAddOptionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "scheduledCallCreationMessage",
        protoKey: "scheduledCallCreationMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "scheduledCallEditMessage",
        protoKey: "scheduledCallEditMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "spoilerMessage",
        protoKey: "spoilerMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "statusAddYours",
        protoKey: "statusAddYours",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "statusMentionMessage",
        protoKey: "statusMentionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "statusQuotedMessage",
        protoKey: "statusQuotedMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "statusStickerInteractionMessage",
        protoKey: "statusStickerInteractionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "stickerSyncRmrMessage",
        protoKey: "stickerSyncRmrMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "viewOnceMessage",
        protoKey: "viewOnceMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "viewOnceMessageV2",
        protoKey: "viewOnceMessageV2",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "viewOnceMessageV2Extension",
        protoKey: "viewOnceMessageV2Extension",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "wrapper",
    },
    {
        id: "limitSharingMessage",
        protoKey: "limitSharingMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "questionMessage",
        protoKey: "questionMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "statusQuestionAnswerMessage",
        protoKey: "statusQuestionAnswerMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "questionResponseMessage",
        protoKey: "questionResponseMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "questionReplyMessage",
        protoKey: "questionReplyMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "groupRootKeyShare",
        protoKey: "groupRootKeyShare",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "paymentReminderMessage",
        protoKey: "paymentReminderMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "splitPaymentMessage",
        protoKey: "splitPaymentMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "splitPaymentUpdateMessage",
        protoKey: "splitPaymentUpdateMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "rootSecretDistributeMessage",
        protoKey: "rootSecretDistributeMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "WA Web dispatch category: RootSecretDistribute",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
    {
        id: "protocolMessage:revoke",
        protoKey: "protocolMessage",
        parserModule: "WAWebParseProtocolSenderRevokeMessageProto",
        waWebName: "revoked",
        kind: "Revoked",
        subtype: "sender",
        condition: "protocolMessage.type === REVOKE (WA Web keys the parser on the stanza edit attr SENDER_REVOKE; admin revoke uses WAWebParseProtocolAdminRevokeMessageProto, subtype admin)",
        when: { path: ["type"], oneOf: ["REVOKE", 0] },
        fieldsKept: [],
        v1TypeString: "revoked",
        role: "protocol",
    },
    {
        id: "protocolMessage",
        protoKey: "protocolMessage",
        parserModule: null,
        waWebName: "protocol",
        kind: "Protocol",
        condition: "other protocolMessage.type values are handled by sub-typed WAWebParseProtocol* parsers; WA Web type protocol",
        fieldsKept: [],
        v1TypeString: "notification",
        role: "protocol",
    },
    {
        id: "audioMessage:ptt",
        protoKey: "audioMessage",
        parserModule: "WAWebPttParsePttMessageProto",
        waWebName: "ptt",
        kind: "Ptt",
        condition: "audioMessage.ptt === true",
        when: { path: ["ptt"], oneOf: [true] },
        fieldsKept: ["backgroundColor", "deprecatedMms3Url", "directPath", "duration", "encFilehash", "filehash", "isViewOnce", "mediaKey", "mediaKeyTimestamp", "mimetype", "size", "streamingSidecar", "waveform"],
        v1TypeString: "ptt",
        role: "content",
    },
    {
        id: "audioMessage",
        protoKey: "audioMessage",
        parserModule: "WAWebPttParseAudioMessageProto",
        waWebName: "audio",
        kind: "Audio",
        fieldsKept: ["backgroundColor", "deprecatedMms3Url", "directPath", "duration", "encFilehash", "filehash", "isViewOnce", "mediaKey", "mediaKeyTimestamp", "mimetype", "size", "streamingSidecar", "waveform"],
        v1TypeString: "audio",
        role: "content",
    },
    {
        id: "buttonsMessage:native_flow",
        protoKey: "buttonsMessage",
        parserModule: "WAWebParseNativeFlowButtonsMessageProto",
        waWebName: "native_flow",
        kind: "NativeFlow",
        condition: "buttons carry a native-flow name (WA Web: getBizNativeFlowName != null); approximated by buttons[0].type === NATIVE_FLOW",
        when: { path: ["buttons", "0", "type"], oneOf: ["NATIVE_FLOW", 2] },
        fieldsKept: ["nativeFlowButtons", "nativeFlowName", "body", "title", "footer", "caption", "headerType"],
        v1TypeString: "chat",
        role: "menu",
        interactiveKind: "native_flow",
        textPaths: [["contentText"]],
        options: { kind: "native-flow", listPath: ["buttons"], nameKey: "nativeFlowInfo.name", paramsKey: "nativeFlowInfo.paramsJson" },
    },
    {
        id: "buttonsMessage",
        protoKey: "buttonsMessage",
        parserModule: "WAWebParseNonNFMButtonsMessageProto",
        waWebName: "chat",
        kind: "Chat",
        condition: "headerType is a known HeaderType (EMPTY..LOCATION); buttons are kept as dynamicReplyButtons; body = contentText, title = text, footer = footerText",
        when: { path: ["headerType"], oneOf: ["EMPTY", 1, "TEXT", 2, "DOCUMENT", 3, "IMAGE", 4, "VIDEO", 5, "LOCATION", 6] },
        fieldsKept: ["body", "title", "footer", "caption", "headerType", "dynamicReplyButtons"],
        v1TypeString: "chat",
        role: "menu",
        interactiveKind: "buttons",
        textPaths: [["contentText"]],
        options: { kind: "buttons", listPath: ["buttons"], idKey: "buttonId", textPath: ["buttonText", "displayText"] },
    },
    {
        id: "buttonsMessage:unsupported",
        protoKey: "buttonsMessage",
        parserModule: "WAWebParseNonNFMButtonsMessageProto",
        waWebName: "unknown",
        kind: "Unknown",
        subtype: "phone_only_feature",
        condition: "unknown/unrecognised headerType (or hasUnsupportedButtons): WA Web floor is unknown",
        fieldsKept: [],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "menu",
        interactiveKind: "buttons",
        textPaths: [["contentText"]],
        options: { kind: "buttons", listPath: ["buttons"], idKey: "buttonId", textPath: ["buttonText", "displayText"] },
    },
    {
        id: "templateMessage:interactive",
        protoKey: "templateMessage",
        parserModule: "WAWebParseTemplateMessageProto",
        waWebName: "interactive",
        kind: "Interactive",
        condition: "templateMessage.interactiveMessageTemplate present (delegates to interactive parsing)",
        when: { path: ["interactiveMessageTemplate"], present: true },
        fieldsKept: ["caption", "interactiveType", "interactivePayload"],
        v1TypeString: "chat",
        role: "menu",
        interactiveKind: "template",
        textPaths: [["interactiveMessageTemplate", "body", "text"]],
    },
    {
        id: "templateMessage",
        protoKey: "templateMessage",
        parserModule: "WAWebParseTemplateMessageProto",
        waWebName: "chat",
        kind: "Chat",
        condition: "hydratedTemplate / hydratedFourRowTemplate: body = hydratedContentText; media parsed as image/document/location/video",
        fieldsKept: ["body", "hydratedButtons"],
        v1TypeString: "chat",
        role: "menu",
        interactiveKind: "template",
        textPaths: [["hydratedTemplate", "hydratedContentText"], ["hydratedFourRowTemplate", "hydratedContentText"]],
    },
    {
        id: "buttonsResponseMessage",
        protoKey: "buttonsResponseMessage",
        parserModule: "WAWebParseButtonsResponseMessageProto",
        waWebName: "buttons_response",
        kind: "ButtonsResponse",
        condition: "buttonsResponseMessage.type === DISPLAY_TEXT; body = selectedDisplayText",
        when: { path: ["type"], oneOf: ["DISPLAY_TEXT", 1] },
        fieldsKept: ["body", "selectedButtonId"],
        v1TypeString: "chat",
        role: "response",
        interactiveKind: "buttons_response",
        textPaths: [["selectedDisplayText"]],
        selection: { idPath: ["selectedButtonId"], textPath: ["selectedDisplayText"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "buttonsResponseMessage:non_display_text",
        protoKey: "buttonsResponseMessage",
        parserModule: "WAWebParseButtonsResponseMessageProto",
        waWebName: "unknown",
        kind: "Unknown",
        condition: "buttonsResponseMessage.type !== DISPLAY_TEXT: WA Web floor is unknown (no body, no selectedButtonId)",
        fieldsKept: [],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "response",
        interactiveKind: "buttons_response",
        textPaths: [["selectedDisplayText"]],
        selection: { idPath: ["selectedButtonId"], textPath: ["selectedDisplayText"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "listMessage",
        protoKey: "listMessage",
        parserModule: "WAWebParseListMessageProto",
        waWebName: "list",
        kind: "List",
        condition: "listMessage.listType in {SINGLE_SELECT, PRODUCT_LIST}",
        when: { path: ["listType"], oneOf: ["SINGLE_SELECT", 1, "PRODUCT_LIST", 2] },
        fieldsKept: ["list", "footer", "businessOwnerJid", "productListItemCount"],
        v1TypeString: "chat",
        role: "menu",
        interactiveKind: "list",
        textPaths: [["description"], ["title"]],
        options: { kind: "list-sections", sectionsPath: ["sections"], rowsKey: "rows", idKey: "rowId", titleKey: "title", descriptionKey: "description", sectionTitleKey: "title" },
    },
    {
        id: "listMessage:unsupported",
        protoKey: "listMessage",
        parserModule: "WAWebParseListMessageProto",
        waWebName: "unknown",
        kind: "Unknown",
        condition: "any other listType: WA Web floor is unknown",
        fieldsKept: [],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "menu",
        interactiveKind: "list",
        textPaths: [["description"], ["title"]],
        options: { kind: "list-sections", sectionsPath: ["sections"], rowsKey: "rows", idKey: "rowId", titleKey: "title", descriptionKey: "description", sectionTitleKey: "title" },
    },
    {
        id: "listResponseMessage",
        protoKey: "listResponseMessage",
        parserModule: "WAWebParseListResponseMessageProto",
        waWebName: "list_response",
        kind: "ListResponse",
        condition: "listResponseMessage.listType === SINGLE_SELECT; body = title + \"\\n\" + description",
        when: { path: ["listType"], oneOf: ["SINGLE_SELECT", 1] },
        fieldsKept: ["body", "listResponse"],
        v1TypeString: "chat",
        role: "response",
        interactiveKind: "list_response",
        textPaths: [["title"]],
        selection: { idPath: ["singleSelectReply", "selectedRowId"], textPath: ["title"], descriptionPath: ["description"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "listResponseMessage:unsupported",
        protoKey: "listResponseMessage",
        parserModule: "WAWebParseListResponseMessageProto",
        waWebName: "unknown",
        kind: "Unknown",
        subtype: "phone_only_feature",
        condition: "any other listType: WA Web floor is unknown/phone_only_feature",
        fieldsKept: [],
        v1TypeString: "chat",
        baileysCanRecover: true,
        role: "response",
        interactiveKind: "list_response",
        textPaths: [["title"]],
        selection: { idPath: ["singleSelectReply", "selectedRowId"], textPath: ["title"], descriptionPath: ["description"], quotedIdPath: ["contextInfo", "stanzaId"], quotedParticipantPath: ["contextInfo", "participant"] },
    },
    {
        id: "secretEncryptedMessage:EVENT_EDIT",
        protoKey: "secretEncryptedMessage",
        parserModule: "WAWebEventsParseEventEditEncryptedMessageProto",
        waWebName: "event_edit_encrypted",
        kind: null,
        condition: "secretEncryptedMessage.secretEncType === EVENT_EDIT",
        when: { path: ["secretEncType"], oneOf: ["EVENT_EDIT"] },
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "secretEncryptedMessage:MESSAGE_EDIT",
        protoKey: "secretEncryptedMessage",
        parserModule: "WAWebParseMessageEditEncryptedMessageProto",
        waWebName: "message_edit_encrypted",
        kind: null,
        condition: "secretEncryptedMessage.secretEncType === MESSAGE_EDIT",
        when: { path: ["secretEncType"], oneOf: ["MESSAGE_EDIT"] },
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "secretEncryptedMessage:POLL_EDIT",
        protoKey: "secretEncryptedMessage",
        parserModule: "WAWebPollsParsePollEditEncryptedMessageProto",
        waWebName: "poll_edit_encrypted",
        kind: null,
        condition: "secretEncryptedMessage.secretEncType === POLL_EDIT",
        when: { path: ["secretEncType"], oneOf: ["POLL_EDIT"] },
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "secretEncryptedMessage:POLL_ADD_OPTION",
        protoKey: "secretEncryptedMessage",
        parserModule: "WAWebPollsParsePollAddOptionEncryptedMessageProto",
        waWebName: "poll_add_option_encrypted",
        kind: null,
        condition: "secretEncryptedMessage.secretEncType === POLL_ADD_OPTION",
        when: { path: ["secretEncType"], oneOf: ["POLL_ADD_OPTION"] },
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "protocol",
    },
    {
        id: "secretEncryptedMessage",
        protoKey: "secretEncryptedMessage",
        parserModule: null,
        waWebName: "unknown",
        kind: null,
        condition: "any other secretEncType: WA dispatch category UnknownMessageKey",
        fieldsKept: [],
        v1TypeString: "unknown",
        role: "unmapped",
    },
] as const;

// Compile-time proof every row fits the interface (works on TS 4.9 and 6).
const WA_ROWS_CHECK: readonly WaMessageTypeRow[] = WA_MESSAGE_TYPE_MAP;

export type WaMessageTypeRowId = (typeof WA_MESSAGE_TYPE_MAP)[number]['id'];
/** Every /v1 type string this table can produce. */
export type WaMessageType = (typeof WA_MESSAGE_TYPE_MAP)[number]['v1TypeString'];

// ── helpers (pure, plain-object input) ───────────────────────────────

type PlainRecord = { readonly [key: string]: unknown };

function isRecord(v: unknown): v is PlainRecord {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function getAt(root: unknown, path: readonly string[]): unknown {
    let cur: unknown = root;
    for (const seg of path) {
        if (Array.isArray(cur)) {
            const i = Number(seg);
            cur = Number.isInteger(i) ? (cur as readonly unknown[])[i] : undefined;
        } else if (isRecord(cur)) {
            cur = cur[seg];
        } else {
            return undefined;
        }
    }
    return cur;
}

function asString(v: unknown): string | null {
    return typeof v === 'string' ? v : null;
}

function asNumber(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const ROWS_BY_KEY: ReadonlyMap<string, readonly WaMessageTypeRow[]> = (() => {
    const m = new Map<string, WaMessageTypeRow[]>();
    for (const row of WA_ROWS_CHECK) {
        const list = m.get(row.protoKey);
        if (list) list.push(row);
        else m.set(row.protoKey, [row]);
    }
    return m;
})();

/** Keys WA Web skips when it picks the "main" content key. */
const IGNORED_KEYS: ReadonlySet<string> = new Set([
    'messageContextInfo',
    'senderKeyDistributionMessage',
    'fastRatchetKeySenderKeyDistributionMessage',
]);

const WRAPPER_KEYS: readonly string[] = WA_ROWS_CHECK.filter((r) => r.role === 'wrapper').map((r) => r.protoKey);

/** Peel envelope wrappers (ephemeral / view-once / device-sent / ...) to the real content. */
export function unwrapProto(content: object): PlainRecord {
    let cur: unknown = content;
    for (let depth = 0; depth < 4 && isRecord(cur); depth++) {
        let inner: unknown;
        for (const key of WRAPPER_KEYS) {
            const candidate = getAt(cur, [key, 'message']);
            if (isRecord(candidate)) {
                inner = candidate;
                break;
            }
        }
        if (inner === undefined) break;
        cur = inner;
    }
    return isRecord(cur) ? cur : {};
}

function whenHolds(when: WaWhen | undefined, proto: unknown): boolean {
    if (!when) return true;
    const v = getAt(proto, when.path);
    if ('present' in when) return (v !== undefined && v !== null) === when.present;
    return (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && when.oneOf.includes(v);
}

export interface WaRowMatch {
    readonly row: WaMessageTypeRow;
    /** The decoded proto value under `row.protoKey`. */
    readonly proto: unknown;
}

/** Find the table row for a decoded proto message (first content key with a row wins). */
export function matchRow(content: object): WaRowMatch | null {
    const unwrapped = unwrapProto(content);
    for (const key of Object.keys(unwrapped)) {
        if (IGNORED_KEYS.has(key)) continue;
        const proto = unwrapped[key];
        if (proto === undefined || proto === null) continue;
        const rows = ROWS_BY_KEY.get(key);
        if (!rows) continue;
        const row = rows.find((r) => whenHolds(r.when, proto));
        if (row) return { row, proto };
    }
    return null;
}

export interface WaClassification {
    /** The /v1 type string to emit. */
    readonly type: string;
    /** What WA Web itself would call it (`unknown` = WA Web's floor). */
    readonly waWebName: string | null;
    readonly subtype: string | null;
    readonly protoKey: string | null;
    readonly rowId: string | null;
    readonly role: WaRole | null;
    /** True when WA Web would show `unknown` but we still carry the data. */
    readonly recovered: boolean;
}

export function classifyProto(content: object): WaClassification {
    const m = matchRow(content);
    if (!m) {
        return { type: 'unknown', waWebName: null, subtype: null, protoKey: null, rowId: null, role: null, recovered: false };
    }
    return {
        type: m.row.v1TypeString,
        waWebName: m.row.waWebName,
        subtype: m.row.subtype ?? null,
        protoKey: m.row.protoKey,
        rowId: m.row.id,
        role: m.row.role,
        recovered: m.row.baileysCanRecover === true,
    };
}

export interface WaOption {
    readonly id: string | null;
    readonly title: string | null;
    readonly description: string | null;
    readonly section: string | null;
    /** native-flow button name (e.g. quick_reply) */
    readonly name: string | null;
}

function parseJsonRecord(raw: unknown): PlainRecord | null {
    if (typeof raw !== 'string' || raw.length === 0) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        return isRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function dotted(key: string): readonly string[] {
    return key.split('.');
}

/** Menu options (buttons / list rows / native-flow buttons) of a menu message; [] otherwise. */
export function projectOptions(content: object): readonly WaOption[] {
    const m = matchRow(content);
    const spec = m?.row.options;
    if (!m || !spec) return [];
    const list = getAt(m.proto, spec.kind === 'list-sections' ? spec.sectionsPath : spec.listPath);
    if (!Array.isArray(list)) return [];
    const out: WaOption[] = [];
    const items: readonly unknown[] = list;
    if (spec.kind === 'buttons') {
        for (const b of items) {
            out.push({ id: asString(getAt(b, [spec.idKey])), title: asString(getAt(b, spec.textPath)), description: null, section: null, name: null });
        }
    } else if (spec.kind === 'list-sections') {
        for (const s of items) {
            const section = asString(getAt(s, [spec.sectionTitleKey]));
            const rows = getAt(s, [spec.rowsKey]);
            if (!Array.isArray(rows)) continue;
            for (const r of rows as readonly unknown[]) {
                out.push({ id: asString(getAt(r, [spec.idKey])), title: asString(getAt(r, [spec.titleKey])), description: asString(getAt(r, [spec.descriptionKey])), section, name: null });
            }
        }
    } else {
        for (const b of items) {
            const params = parseJsonRecord(getAt(b, dotted(spec.paramsKey)));
            out.push({
                id: asString(params?.id),
                title: asString(params?.display_text) ?? asString(params?.title),
                description: null,
                section: null,
                name: asString(getAt(b, dotted(spec.nameKey))),
            });
        }
    }
    return out;
}

export interface WaSelection {
    readonly id: string | null;
    readonly text: string | null;
    readonly description: string | null;
    readonly index: number | null;
    /** native-flow response name */
    readonly name: string | null;
    /** stanzaId of the menu message the user answered */
    readonly quotedMessageId: string | null;
    readonly quotedParticipant: string | null;
}

/** The option the user picked (button / list row / template / native-flow response); null if not a response. */
export function projectSelection(content: object): WaSelection | null {
    const m = matchRow(content);
    const spec = m?.row.selection;
    if (!m || !spec) return null;
    const json = spec.jsonPath ? parseJsonRecord(getAt(m.proto, spec.jsonPath)) : null;
    const jsonId = spec.jsonIdKey && json ? asString(json[spec.jsonIdKey]) : null;
    return {
        id: (spec.idPath.length > 0 ? asString(getAt(m.proto, spec.idPath)) : null) ?? jsonId,
        text: asString(getAt(m.proto, spec.textPath)),
        description: spec.descriptionPath ? asString(getAt(m.proto, spec.descriptionPath)) : null,
        index: spec.indexPath ? asNumber(getAt(m.proto, spec.indexPath)) : null,
        name: spec.namePath ? asString(getAt(m.proto, spec.namePath)) : null,
        quotedMessageId: asString(getAt(m.proto, spec.quotedIdPath)),
        quotedParticipant: asString(getAt(m.proto, spec.quotedParticipantPath)),
    };
}

/** Message text for a menu/click (contentText / selectedDisplayText / list title ...); null when the row has no text path. */
export function projectText(content: object): string | null {
    const m = matchRow(content);
    const paths = m?.row.textPaths;
    if (!m || !paths) return null;
    for (const p of paths) {
        const v = asString(getAt(m.proto, p));
        if (v !== null && v.length > 0) return v;
    }
    return null;
}

export interface WaInteractiveOption {
    readonly id: string | null;
    readonly title: string | null;
    readonly description?: string;
    readonly section?: string;
}

/** The optional  block of the /v1 message (menu options or the click). */
export interface WaInteractive {
    readonly kind: WaInteractiveKind;
    readonly options?: WaInteractiveOption[];
    readonly selectedId?: string;
    readonly selectedTitle?: string;
    readonly quotedId?: string;
}

/** menu -> {kind, options}; click -> {kind, selectedId, selectedTitle, quotedId}; anything else -> null. */
export function projectInteractive(content: object): WaInteractive | null {
    const m = matchRow(content);
    const kind = m?.row.interactiveKind;
    if (!m || !kind) return null;
    if (m.row.role === 'response') {
        const sel = projectSelection(content);
        return {
            kind,
            ...(sel?.id != null ? { selectedId: sel.id } : {}),
            ...(sel?.text != null ? { selectedTitle: sel.text } : {}),
            ...(sel?.quotedMessageId != null ? { quotedId: sel.quotedMessageId } : {}),
        };
    }
    const opts = projectOptions(content);
    return {
        kind,
        ...(opts.length > 0
            ? {
                  options: opts.map((o) => ({
                      id: o.id,
                      title: o.title,
                      ...(o.description != null ? { description: o.description } : {}),
                      ...(o.section != null ? { section: o.section } : {}),
                  })),
              }
            : {}),
    };
}

// ── waType canonical / v1 type deprecated ────────────────────────────

/**
 * Message type filters accept BOTH names: the WA Web name (canonical, e.g.
 * 'buttons_response', 'poll_creation') and the deprecated v1 string (e.g. 'chat',
 * 'poll', 'ptt'). Returns every WA Web type (`waType`) the name denotes, so
 * `message_types=chat` (legacy) still selects menus/clicks like v1 does, while
 * `message_types=buttons_response` selects only clicks. Empty array = unknown name.
 */
export function resolveMessageTypeFilter(name: string): readonly string[] {
    const out = new Set<string>();
    for (const r of WA_ROWS_CHECK) {
        if (r.waWebName === null) continue;
        if (r.waWebName === name || r.v1TypeString === name || r.legacyBaileysType === name) out.add(r.waWebName);
    }
    return [...out];
}

/** The deprecated v1 `type` string derived from a WA Web type (first row wins; menus/clicks map to 'chat'). */
export function v1TypeForWaType(waType: string): string | null {
    const row = WA_ROWS_CHECK.find((r) => r.waWebName === waType);
    return row ? row.v1TypeString : null;
}

/** The v1 `message_types` names the engines have always accepted (and `gif`, which the engines special-case). */
const LEGACY_FILTER_NAMES: ReadonlySet<string> = new Set([
    'chat', 'image', 'video', 'document', 'audio', 'ptt', 'sticker', 'gif', 'ptv', 'poll_creation', 'location', 'vcard', 'revoked',
]);

/** True when `name` is a legacy v1 filter name or a WA Web type the table knows (`unknown` is not filterable). */
export function isMessageTypeFilterName(name: string): boolean {
    if (name === 'unknown') return false;
    return LEGACY_FILTER_NAMES.has(name) || resolveMessageTypeFilter(name).length > 0;
}

// ── outbound click: validate a reply against the stored menu ─────────

export type ButtonReplyResolution =
    | { readonly ok: true; readonly reply: ButtonReply }
    | { readonly ok: false; readonly reason: 'not_a_menu' | 'unsupported_menu' | 'unknown_option' };

/**
 * Resolve a click against the menu it answers, from the menu's decoded proto (the stored message). Returns
 * the reply with `kind`/`title`/`description`/`index` DERIVED from the menu (a caller never supplies them),
 * or why not: `not_a_menu` (plain text / a click / anything without options), `unsupported_menu` (a menu
 * whose reply proto we do not build: native flow, interactive, template, or one WA Web itself cannot render),
 * `unknown_option` (the id is not one of the menu's options).
 */
export function resolveButtonReply(menuContent: object, optionId: string): ButtonReplyResolution {
    const m = matchRow(menuContent);
    if (!m || m.row.role !== 'menu') return { ok: false, reason: 'not_a_menu' };
    const kind = m.row.interactiveKind;
    if ((kind !== 'buttons' && kind !== 'list') || m.row.waWebName === 'unknown') return { ok: false, reason: 'unsupported_menu' };
    const options = projectOptions(menuContent);
    const index = options.findIndex((o) => o.id === optionId);
    if (index < 0) return { ok: false, reason: 'unknown_option' };
    const option = options[index];
    if (option.title === null || option.title === '') return { ok: false, reason: 'unknown_option' };
    if (kind === 'list') {
        return { ok: true, reply: { kind: 'list', id: optionId, title: option.title, ...(option.description !== null ? { description: option.description } : {}) } };
    }
    return { ok: true, reply: { kind: 'buttons', id: optionId, title: option.title, index } };
}
